// assets/dcg-events.js
(() => {
  "use strict";

  function qs(sel, root = document) {
    return root.querySelector(sel);
  }

  // --- Scroll-reveal entrance ---
  const section = qs('.dcg-events[data-dcg-events]');
  if (section) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      section.classList.add('is-visible');
    } else {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              section.classList.add('is-visible');
              obs.unobserve(section);
            }
          });
        },
        { threshold: 0.08 }
      );
      obs.observe(section);
    }
  }

  // --- Ensure DCG Cart Bridge exists ---
  // Top Singles creates this; we reuse it if available,
  // otherwise create our own identical copy.
  (function ensureDcgCartBridge() {
    window.DCG = window.DCG || {};
    window.DCG.cart = window.DCG.cart || {};
    if (typeof window.DCG.cart.addItem === "function") return;

    window.DCG.cart.addItem = async function ({ id, quantity = 1, properties } = {}) {
      if (!id) throw new Error("Missing variant id");

      // ── Pre-flight enforcement (mirrors dcg-cart-bridge.js) ────────────────
      // Applies the same stock/purchase-limit rules as every other add path, even
      // on pages where the full cart bridge isn't loaded. window.DCG.enforcer is
      // defined globally by dcg-cart-enforcer.js in <head>. Without this, event
      // adds skipped enforcement and behavior depended on script load order.
      if (window.DCG && typeof window.DCG.enforcer === "object" && window.DCG.enforcer) {
        let _enforcerBlocked = false;
        try {
          const check = await window.DCG.enforcer.preflight(id, quantity);
          if (check.blocked) {
            _enforcerBlocked = true;
            window.DCG.enforcer.showToast(check.message || "Purchase limit reached.");
            throw new Error(check.message || "Purchase limit reached.");
          }
          if (check.message) {
            window.DCG.enforcer.showToast(check.message);
          }
          quantity = check.qty;
        } catch (enforceErr) {
          if (_enforcerBlocked) throw enforceErr;
          console.warn("[DCG] enforcer preflight error — allowing add:", enforceErr);
        }
      }

      const payload = { id: Number(id), quantity: Number(quantity || 1) };
      if (properties) payload.properties = properties;

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data?.description || data?.message || "Add to cart failed");
        } catch (_) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Add to cart failed");
        }
      }

      const data = await res.json();

      if (typeof window.adjustCartDropDown === "function") {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }

      return data;
    };
  })();


  // --- ATC Submit Interceptor ---
  // Intercepts event card forms, uses DCG cart bridge + $.notify() toast
  // Matches the exact same pattern as Top Singles for consistency.

  const EVENT_FORM_CLASS = "dcg-event__cartForm";

  document.addEventListener("submit", async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains(EVENT_FORM_CLASS)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const btn = form.querySelector('button[type="submit"]');
    const idInput = qs('input[name="id"]', form);
    const variantId = idInput?.value;

    if (!variantId) return;

    const originalHTML = btn ? btn.innerHTML : "";
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="dcg-event__ctaText">Adding\u2026</span>';
    }

    try {
      const item = await window.DCG.cart.addItem({ id: variantId, quantity: 1 });

      // Theme-native $.notify() toast — green success (matches Top Singles)
      if (typeof $ !== "undefined" && typeof $.notify === "function") {
        const successPrefix = $("#cartmessage .title-success").text() || "Added to cart: ";
        const link = '<a href="' + (item.url || "") + '">' + (item.title || "Item") + '</a>';
        const msg = successPrefix + link + " - " + (item.quantity || 1) + " x " +
          (typeof Shopify !== "undefined" && Shopify.formatMoney
            ? Shopify.formatMoney(item.price, Shopify.money_format)
            : "");

        $.notify(
          { message: msg },
          {
            type: "success",
            offset: 0,
            placement: { from: "top", align: "center" },
            z_index: 9999,
            animate: { enter: "animated fadeInDown", exit: "animated fadeOutUp" },
            template:
              '<div data-notify="container" class="col-xs-12 alert alert-{0}" role="alert">' +
              '<button type="button" aria-hidden="true" class="close" data-notify="dismiss">\u00d7</button>' +
              '<span data-notify="icon"></span> <span data-notify="title">{1}</span> ' +
              '<span data-notify="message">{2}</span>' +
              '<div class="progress" data-notify="progressbar">' +
              '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
              '</div><a href="{3}" target="{4}" data-notify="url"></a></div>',
          }
        );
      }

      // Update the card's addedNotice if present
      const notice = form.closest(".dcg-event")?.querySelector(".dcg-event__addedNotice");
      if (notice) {
        notice.textContent = "\u2713 Added to cart";
        setTimeout(() => { notice.textContent = ""; }, 3000);
      }

    } catch (err) {
      console.warn("[DCG Events] Add error:", err);

      // Theme-native error toast — red danger (matches Top Singles)
      if (typeof $ !== "undefined" && typeof $.notify === "function") {
        const failPrefix = $("#cartmessage .title-failed").text() || "Could not add: ";
        $.notify(
          { message: failPrefix + (err?.message || "Please try again.") },
          {
            type: "danger",
            offset: 0,
            placement: { from: "top", align: "center" },
            z_index: 9999,
            animate: { enter: "animated fadeInDown", exit: "animated fadeOutUp" },
            template:
              '<div data-notify="container" class="col-xs-12 alert alert-{0}" role="alert">' +
              '<button type="button" aria-hidden="true" class="close" data-notify="dismiss">\u00d7</button>' +
              '<span data-notify="icon"></span> <span data-notify="title">{1}</span> ' +
              '<span data-notify="message">{2}</span>' +
              '<div class="progress" data-notify="progressbar">' +
              '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
              '</div><a href="{3}" target="{4}" data-notify="url"></a></div>',
          }
        );
      }
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    }
  }, true); // capture phase — same as Top Singles


  // --- Variant select sync (for event cards with condition dropdowns) ---
  const EVENT_CARD_SELECTOR = '.dcg-event';

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-dcg-variant-select]');
    if (!select) return;

    const card = select.closest(EVENT_CARD_SELECTOR);
    if (!card) return;

    const idInput =
      qs('input[name="id"]', card) ||
      qs('[data-dcg-variant-input][name="id"]', card);

    if (idInput) idInput.value = select.value;

    const opt = select.options[select.selectedIndex];
    const title = (opt?.dataset?.title || opt?.textContent || '').trim();
    const label = qs('[data-dcg-selected-variant]', card);
    if (label && title) label.textContent = title;

    const avail = opt?.dataset?.available;
    const btn = qs('button[type="submit"]', card);
    if (btn && avail != null) {
      btn.disabled = avail !== '1';
    }
  });
})();
